// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';
import parsePhoneNumber from 'libphonenumber-js';
import PropTypes from 'prop-types';

import * as Yup from 'yup';

import { useFormik } from 'formik';
import OrLineDivider from './OrLineDivider';

import {
	SUPPORT_TOPICS,
	SUPPORT_MODAL_STATE,
	EKHO_PHONE_SUPPORT_SCHEDULE,
} from '../utils/constants';
import { supportIsAvailable, formatHMMTime } from '../utils/util_funcs';

import Modal, { ModalHeader, ModalTitle, ModalFooter, ModalBody } from './bootstrap/Modal';

import Button from './bootstrap/Button';
import Icon from './icon/Icon';

import FormGroup from './bootstrap/forms/FormGroup';

import Input from './bootstrap/forms/Input';
import Textarea from './bootstrap/forms/Textarea';
import Select from './bootstrap/forms/Select';
import Spinner from './bootstrap/Spinner';

// import Button from './bootstrap/Button';
// import Card, { CardTitle, CardLabel } from './bootstrap/Card';
// import OrLineDivider from './OrLineDivider';

// import { SUPPORT_TOPICS } from '../utils/constants';

const SupportHubContent = ({
	excludedTopics,
	activeSupportTopicKey,
	setActiveSupportTopicKey,
	setSupportModalState,
	oem,
	ekhoChatOnClick,
	toggleModal,
	modalOpen,
	style,
}) => {
	let oemPhoneNumberObject = null;
	if (oem.numberForSupport) {
		oemPhoneNumberObject = parsePhoneNumber(oem.numberForSupport);
	}
	let ekhoPhoneNumberObject = null;
	if (oem.ekhoNumberForSupport) {
		ekhoPhoneNumberObject = parsePhoneNumber(oem.ekhoNumberForSupport);
	} else {
		ekhoPhoneNumberObject = parsePhoneNumber('+13025400294');
	}
	const currentDay = new Date().toLocaleString('en-us', { weekday: 'long' }).toLowerCase();
	const { isAvailable: ekhoPhoneSupportAvailableNow, nextOnline: ekhoNextOnline } =
		supportIsAvailable(EKHO_PHONE_SUPPORT_SCHEDULE);
	const currentDayEkhoSupportInfo = EKHO_PHONE_SUPPORT_SCHEDULE[currentDay] || null;
	let ekhoSupportTimeString = ekhoPhoneSupportAvailableNow
		? 'Available now. '
		: 'Not currently available. ';
	if (
		currentDayEkhoSupportInfo &&
		currentDayEkhoSupportInfo.start &&
		currentDayEkhoSupportInfo.end
	) {
		ekhoSupportTimeString += `Hours: ${formatHMMTime(
			currentDayEkhoSupportInfo.start,
		)} to ${formatHMMTime(currentDayEkhoSupportInfo.end)} EST`;
	} else if (ekhoNextOnline) {
		ekhoSupportTimeString += ekhoNextOnline;
	}
	const supportSchedule = oem.supportSchedule || {};
	const { isAvailable: oemPhoneSupportAvailableNow, nextOnline: oemNextOnline } =
		supportIsAvailable(supportSchedule);
	const currentDayOemSupportInfo = supportSchedule[currentDay] || null;
	let oemSupportTimeString = oemPhoneSupportAvailableNow
		? 'Available now. '
		: 'Not currently available. ';
	if (
		currentDayOemSupportInfo &&
		currentDayOemSupportInfo.start &&
		currentDayOemSupportInfo.end
	) {
		oemSupportTimeString += `Hours: ${formatHMMTime(
			currentDayOemSupportInfo.start,
		)} to ${formatHMMTime(currentDayOemSupportInfo.end)} EST`;
	} else if (oemNextOnline) {
		oemSupportTimeString += oemNextOnline;
	}
	return (
		<Modal
			isOpen={modalOpen}
			setIsOpen={toggleModal}
			isScrollable
			isCentered
			size='lg'
			isStaticBackdrop={false}
			fullScreen='sm'
			isAnimation={false}>
			<ModalHeader
				className='d-flex justify-content-between'
				setIsOpen={toggleModal}
				headerClose>
				<ModalTitle
					id='modalTitle'
					className='h3 d-flex justify-content-end'
					style={{ textAlign: 'left' }}>
					What do you need help with?
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<div className='row g-3'>
					{/* map sections to an aboutEkhoStep */}
					{Object.values(SUPPORT_TOPICS)
						.filter(({ key }) => {
							return excludedTopics && !excludedTopics.includes(key);
						})
						.map(({ key, title }) => (
							<div className='col-auto' key={`${key}`}>
								<Button
									color='info'
									size='sm'
									key={`${key}`}
									isLight={key !== activeSupportTopicKey}
									onClick={() => {
										setActiveSupportTopicKey(key);
									}}>
									{title}
								</Button>
							</div>
						))}
				</div>
				<br />
				<ModalTitle tag='h4' className='h4'>
					Contact{' '}
					{SUPPORT_TOPICS[activeSupportTopicKey].commsOwnedByEkho
						? 'Ekho'
						: `${oem.name}`}{' '}
					regarding {SUPPORT_TOPICS[activeSupportTopicKey].title.toLowerCase()}
				</ModalTitle>
				<p className='text-muted lightSubtitleText'>
					{SUPPORT_TOPICS[activeSupportTopicKey]?.description || ''}
				</p>
				<br />
				{/* Set up rows that each have an icon on the left and then text to the right */}
				{SUPPORT_TOPICS[activeSupportTopicKey].commsOwnedByEkho && (
					<>
						<SupportOption
							onClickFunction={() => {
								ekhoChatOnClick(activeSupportTopicKey);
							}}
							secondaryCopy='Available now.'
							buttonCopy='Chat with Ekho'
							ctaCopy={`Chat with the ${oem.name} support team at Ekho`}
							iconName='ChatBubble'
							style={style}
						/>

						<React.Fragment key='ekhoNumberForSupportOrDivider'>
							<OrLineDivider />
						</React.Fragment>
						<SupportOption
							onClickFunction={() => {
								// call the OEM number programmatically
								window.location.href = ekhoPhoneNumberObject
									? ekhoPhoneNumberObject.getURI()
									: 'tel:+13025400294';
							}}
							secondaryCopy={ekhoSupportTimeString}
							buttonDisabled={!ekhoPhoneSupportAvailableNow}
							buttonCopy={
								ekhoPhoneSupportAvailableNow
									? `Call 
							${ekhoPhoneNumberObject ? ekhoPhoneNumberObject.formatInternational() : '+1 (302) 540-0294'}`
									: 'Call Ekho'
							}
							ctaCopy={`Call the ${oem.name} support team at Ekho`}
							iconName={ekhoPhoneSupportAvailableNow ? 'LocalPhone' : 'PhoneDisabled'}
							accentColor={ekhoPhoneSupportAvailableNow ? 'info' : 'dark'}
							style={style}
						/>
					</>
				)}
				{!SUPPORT_TOPICS[activeSupportTopicKey].commsOwnedByEkho && (
					<>
						{oem.emailForSupport && (
							<>
								<SupportOption
									onClickFunction={() => {
										setSupportModalState(SUPPORT_MODAL_STATE.MESSAGE_OEM);
									}}
									secondaryCopy='Available now.'
									buttonCopy={`Message ${oem.name}`}
									ctaCopy={`Message the ${oem.name} support team. You will receive replies via email.`}
									iconName='ChatBubble'
									style={style}
								/>
								{oem.numberForSupport && (
									<React.Fragment key='emailForSupportOrDivider'>
										<OrLineDivider />
									</React.Fragment>
								)}
							</>
						)}
						{oem.numberForSupport && (
							<SupportOption
								onClickFunction={() => {
									// call the OEM number programmatically
									window.location.href = oemPhoneNumberObject
										? oemPhoneNumberObject.getURI()
										: `tel:${oem.numberForSupport}`;
								}}
								secondaryCopy={oemSupportTimeString}
								buttonDisabled={!oemPhoneSupportAvailableNow}
								buttonCopy={
									oemPhoneSupportAvailableNow
										? `Call 
							${oemPhoneNumberObject ? oemPhoneNumberObject.formatInternational() : `${oem.numberForSupport}`}`
										: `Call ${oem.name} support`
								}
								ctaCopy={`Call the ${oem.name} support team`}
								iconName={
									oemPhoneSupportAvailableNow ? 'LocalPhone' : 'PhoneDisabled'
								}
								accentColor={oemPhoneSupportAvailableNow ? 'info' : 'dark'}
								style={style}
							/>
						)}
					</>
				)}
				{/* Add a 3rem space at the bottom */}
				<div style={{ height: '3rem' }} />
			</ModalBody>
		</Modal>
	);
};

SupportHubContent.propTypes = {
	excludedTopics: PropTypes.array.isRequired,
	activeSupportTopicKey: PropTypes.string.isRequired,
	setActiveSupportTopicKey: PropTypes.func.isRequired,
	setSupportModalState: PropTypes.func.isRequired,
	oem: PropTypes.object.isRequired,
	ekhoChatOnClick: PropTypes.func.isRequired,
	toggleModal: PropTypes.func.isRequired,
	modalOpen: PropTypes.bool.isRequired,
	style: PropTypes.object,
};

SupportHubContent.defaultProps = {
	style: {},
};

const MessageOemSupportContent = ({
	sendMessageToOem,
	supportTopicKey,
	oem,
	toggleModal,
	setSupportModalState,
	modalOpen,
	buyerEmail,
	buyerFullName,
}) => {
	const [buttonLoading, setButtonLoading] = useState(false);
	const supportTopicsOptions = Object.values(SUPPORT_TOPICS)
		.filter(({ commsOwnedByEkho }) => !commsOwnedByEkho)
		.map(({ key, title }) => ({
			value: key,
			text: title,
		}));

	const messageToOemFormik = useFormik({
		initialValues: {
			supportTopicKey: supportTopicKey || SUPPORT_TOPICS.VEHICLE.key,
			email: buyerEmail || '',
			fullName: buyerFullName || '',
			message: '',
		},
		validationSchema: Yup.object().shape({
			email: Yup.string().email('Invalid email').required('Email is required'),
			fullName: Yup.string().required('First name is required'),
			// make sure message is not empty and is shorter than 800 characters
			message: Yup.string()
				.required('Message is required')
				.max(800, 'Message is too long. Max 800 characters'),
			supportTopicKey: Yup.string().required('Support topic is required'),
		}),
	});
	return (
		<Modal
			isOpen={modalOpen}
			setIsOpen={toggleModal}
			isScrollable
			isCentered
			size='lg'
			isStaticBackdrop={false}
			fullScreen='sm'
			isAnimation={false}>
			<ModalHeader
				className='d-flex justify-content-between'
				setIsOpen={toggleModal}
				headerClose>
				<ModalTitle
					id='modalTitle'
					className='h3 d-flex justify-content-end'
					style={{ textAlign: 'left' }}>
					Message {oem.name} support
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<div className='col-12 text-muted'>
					<p className='text-muted'>
						Replies will be sent to the email you provide below
					</p>
				</div>
				{/* Add an h5 title that says 'personal info' */}
				<div className='row g-4'>
					<div className='col-6'>
						<FormGroup id='email' label='Email address'>
							<Input
								size='lg'
								placeholder=''
								autoComplete='email'
								required
								onChange={messageToOemFormik.handleChange}
								onBlur={messageToOemFormik.handleBlur}
								value={messageToOemFormik.values.email}
								isValid={messageToOemFormik.isValid}
								isTouched={messageToOemFormik.touched.email}
								invalidFeedback={messageToOemFormik.errors.email}
							/>
						</FormGroup>
					</div>
					<div className='col-6'>
						<FormGroup id='fullName' label='Full name'>
							<Input
								size='lg'
								placeholder=''
								autoComplete='given-name'
								required
								onChange={messageToOemFormik.handleChange}
								onBlur={messageToOemFormik.handleBlur}
								value={messageToOemFormik.values.fullName}
								isValid={messageToOemFormik.fullName}
								isTouched={messageToOemFormik.touched.fullName}
								invalidFeedback={messageToOemFormik.errors.fullName}
							/>
						</FormGroup>
					</div>
					{/* Add a Select for the support topic */}
					<div className='col-12'>
						<FormGroup id='supportTopicKey' label='Support topic'>
							<Select
								size='lg'
								placeholder='Select a support topic'
								// only show the support topics where commsOwnedByEkho is false
								list={supportTopicsOptions}
								onChange={messageToOemFormik.handleChange}
								value={messageToOemFormik.values.supportTopicKey}
								isValid={messageToOemFormik.isValid}
								isTouched={messageToOemFormik.touched.supportTopicKey}
								invalidFeedback={messageToOemFormik.errors.supportTopicKey}
							/>
						</FormGroup>
						{/* Add a text area */}
					</div>
					<div className='col-12'>
						<FormGroup id='message' label='Message'>
							<Textarea
								value={messageToOemFormik.values.message}
								onChange={messageToOemFormik.handleChange}
								onBlur={messageToOemFormik.handleBlur}
								isValid={messageToOemFormik.isValid}
								isTouched={messageToOemFormik.touched.message}
								invalidFeedback={messageToOemFormik.errors.message}
								required
								placeholder='Type your message here'
								size='lg'
							/>
						</FormGroup>
					</div>
				</div>
			</ModalBody>
			<ModalFooter className='d-flex justify-content-center footer-top-border-gray'>
				<div className='col-12' style={{ paddingTop: '1rem' }}>
					<p className='text-muted table-section-subtitle'>
						By sending this message, you agree to Ekho Dealer sharing your information
						with
						{` ${oem.name}`} for the purpose of providing support, and for {oem.name} to
						contact you via email.
					</p>
				</div>
				<div className='col-12'>
					<div className=' d-flex flex-row'>
						{!buttonLoading && (
							<div className='w-100'>
								<Button
									className='w-100 p-3'
									style={{ marginLeft: 0, marginRight: 0 }}
									color='info'
									onClick={async () => {
										setButtonLoading(true);
										try {
											await sendMessageToOem(messageToOemFormik.values);
											setButtonLoading(false);
											setSupportModalState(SUPPORT_MODAL_STATE.MESSAGE_SENT);
										} catch (e) {
											console.error(e);
											setButtonLoading(false);
											// show an alert in the browser saying it failed
											alert(
												'Failed to send message. Please check your internet and try again.',
											);
										}
									}}
									isDisable={
										!messageToOemFormik.isValid ||
										!messageToOemFormik.dirty ||
										buttonLoading
									}>
									Send message
								</Button>
							</div>
						)}
						{buttonLoading && (
							<div className='w-100'>
								<Button
									className='w-100 p-3 mt-1 mb-1'
									style={{ marginLeft: 0, marginRight: 0 }}
									color='info'
									isDisable>
									<Spinner isSmall inButton color='light' />
								</Button>
							</div>
						)}
					</div>
				</div>
				<div className='col-12'>
					<div className=' d-flex flex-row'>
						<div className='w-100'>
							<Button
								className='w-100 p-3'
								color='info'
								isOutline
								isDisable={buttonLoading}
								onClick={() => {
									setSupportModalState(SUPPORT_MODAL_STATE.SUPPORT_HUB);
								}}>
								Back to support hub
							</Button>
						</div>
					</div>
				</div>
			</ModalFooter>
		</Modal>
	);
};

MessageOemSupportContent.propTypes = {
	sendMessageToOem: PropTypes.func.isRequired,
	supportTopicKey: PropTypes.string.isRequired,
	oem: PropTypes.object.isRequired,
	toggleModal: PropTypes.func.isRequired,
	setSupportModalState: PropTypes.func.isRequired,
	modalOpen: PropTypes.bool.isRequired,
	buyerEmail: PropTypes.string,
	buyerFullName: PropTypes.string,
};

MessageOemSupportContent.defaultProps = {
	buyerEmail: '',
	buyerFullName: '',
};

const SupportOption = ({
	onClickFunction,
	buttonCopy,
	ctaCopy,
	iconName,
	secondaryCopy,
	buttonDisabled,
	accentColor,
	style,
}) => {
	return (
		<div
			style={{
				border: '1px solid #e9ecef',
				padding: '1rem',
				boxShadow: '0 1px 5px rgba(0, 0, 0, 0.07)',
				...style,
			}}>
			<div className='row col-12'>
				<div className='col-10 d-flex flex-column align-items-start'>
					<p className='body'>{ctaCopy}</p>
					{secondaryCopy && (
						<p className='text-muted lightSecondaryText'>{secondaryCopy}</p>
					)}
					{!buttonDisabled && (
						<Button
							color={accentColor}
							isOutline
							isDisable={buttonDisabled}
							size='sm'
							onClick={async () => {
								onClickFunction();
							}}>
							{buttonCopy}
						</Button>
					)}
				</div>
				<div className='col-2 d-flex flex-column justify-content-center align-items-end'>
					<Icon
						icon={iconName}
						color={accentColor}
						styles={{ height: '3rem', width: '3rem' }}
					/>
				</div>
			</div>
		</div>
	);
};

SupportOption.propTypes = {
	onClickFunction: PropTypes.func.isRequired,
	buttonCopy: PropTypes.string.isRequired,
	ctaCopy: PropTypes.string.isRequired,
	iconName: PropTypes.string.isRequired,
	secondaryCopy: PropTypes.string,
	buttonDisabled: PropTypes.bool,
	accentColor: PropTypes.string,
	style: PropTypes.object,
};

SupportOption.defaultProps = {
	accentColor: 'info',
	secondaryCopy: '',
	buttonDisabled: false,
	style: {},
};

const SupportMessageSentContent = ({ toggleModal, oem, setSupportModalState, modalOpen }) => {
	return (
		<Modal
			isOpen={modalOpen}
			setIsOpen={toggleModal}
			isScrollable
			isCentered
			size='lg'
			isStaticBackdrop
			disableEscape
			fullScreen='sm'
			isAnimation={false}>
			<ModalHeader
				className='d-flex justify-content-between'
				setIsOpen={toggleModal}
				headerClose={false}>
				<ModalTitle
					id='modalTitle'
					className='h3 d-flex justify-content-end'
					style={{ textAlign: 'left' }}>
					Your message has been sent to {oem.name}
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<div className='col-12 text-muted'>
					<p className='text-muted'>
						You can expect to receive a response from {oem.name} at the email address
						you provided.
					</p>
					<div className='col-12 text-center p-3' style={{ fontSize: '6em' }}>
						<Icon icon='MarkEmailRead' color='info' size='xxl' />
					</div>
				</div>
			</ModalBody>
			<ModalFooter className='d-flex justify-content-center'>
				<div className='col-12'>
					<div className=' d-flex flex-row'>
						<div className='w-100'>
							<Button
								className='w-100 p-3'
								color='info'
								onClick={() => {
									toggleModal(false);
									setSupportModalState(SUPPORT_MODAL_STATE.SUPPORT_HUB);
								}}>
								Close
							</Button>
						</div>
					</div>
				</div>
			</ModalFooter>
		</Modal>
	);
};

SupportMessageSentContent.propTypes = {
	toggleModal: PropTypes.func.isRequired,
	modalOpen: PropTypes.bool.isRequired,
	oem: PropTypes.object.isRequired,
	setSupportModalState: PropTypes.func.isRequired,
};

const SupportAndContactModal = ({
	excludedTopics,
	modalOpen,
	toggleModal,
	oem,
	messageOemOnClick,
	ekhoChatOnClick,
	buyerEmail,
	buyerFullName,
	style,
}) => {
	const [supportModalState, setSupportModalState] = useState(SUPPORT_MODAL_STATE.SUPPORT_HUB);
	const [activeSupportTopicKey, setActiveSupportTopicKey] = useState(SUPPORT_TOPICS.PAYMENT.key);
	return (
		<>
			{supportModalState === SUPPORT_MODAL_STATE.SUPPORT_HUB && (
				<SupportHubContent
					excludedTopics={excludedTopics}
					activeSupportTopicKey={activeSupportTopicKey}
					setActiveSupportTopicKey={setActiveSupportTopicKey}
					oem={oem}
					ekhoChatOnClick={ekhoChatOnClick}
					setSupportModalState={setSupportModalState}
					modalOpen={modalOpen}
					toggleModal={toggleModal}
					style={style}
				/>
			)}
			{supportModalState === SUPPORT_MODAL_STATE.MESSAGE_OEM && (
				<MessageOemSupportContent
					sendMessageToOem={messageOemOnClick}
					supportTopicKey={activeSupportTopicKey}
					oem={oem}
					toggleModal={toggleModal}
					setSupportModalState={setSupportModalState}
					modalOpen={modalOpen}
					buyerEmail={buyerEmail}
					buyerFullName={buyerFullName}
				/>
			)}
			{supportModalState === SUPPORT_MODAL_STATE.MESSAGE_SENT && (
				<SupportMessageSentContent
					toggleModal={toggleModal}
					oem={oem}
					setSupportModalState={setSupportModalState}
					modalOpen={modalOpen}
				/>
			)}
		</>
	);
};

SupportAndContactModal.propTypes = {
	excludedTopics: PropTypes.array,
	modalOpen: PropTypes.bool.isRequired,
	toggleModal: PropTypes.func.isRequired,
	oem: PropTypes.object.isRequired,
	// ekhoApp: PropTypes.string.isRequired,
	// checkoutCart: PropTypes.object,
	// purchasedVehicle: PropTypes.object,
	messageOemOnClick: PropTypes.func,
	ekhoChatOnClick: PropTypes.func.isRequired,
	buyerEmail: PropTypes.string,
	buyerFullName: PropTypes.string,
	style: PropTypes.object,
};

// default props
SupportAndContactModal.defaultProps = {
	excludedTopics: [],
	messageOemOnClick: () => {},
	buyerEmail: '',
	buyerFullName: '',
	// checkoutCart: null,
	// purchasedVehicle: null,
	style: {},
};

export default SupportAndContactModal;
